import { FC } from 'react';
import { PROFILE_SECTION_PATHS } from '../utilities/profileCompleteness';

type ProfileReadinessBannerProps = {
  readinessPercent: number;
  nextSection:
    | 'basicPersonalInfo'
    | 'travelDocuments'
    | 'diveCertificate'
    | null;
  missingCount: number;
  onFinish: (path: string) => void;
};

const ProfileReadinessBanner: FC<ProfileReadinessBannerProps> = ({
  readinessPercent,
  nextSection,
  missingCount,
  onFinish,
}) => {
  const isReady = readinessPercent >= 100;
  const finishPath = nextSection
    ? PROFILE_SECTION_PATHS[nextSection]
    : '/profile/basic-personal-info';

  return (
    <div className="mt-6 w-full rounded-2xl border border-neutral-100 bg-neutral-00 px-5 py-4 md:px-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-semibold text-neutral-800">
              {readinessPercent}%
            </span>
            <span className="text-sm text-neutral-500">ready for booking</span>
          </div>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-neutral-100">
            <div
              className="h-full rounded-full bg-brand-secondary transition-[width] duration-300"
              style={{
                width: `${Math.min(100, Math.max(0, readinessPercent))}%`,
              }}
              role="progressbar"
              aria-valuenow={readinessPercent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Profile readiness"
            />
          </div>
          <p className="mt-2 text-sm text-neutral-500">
            {isReady
              ? 'Your profile has everything needed before a trip.'
              : `${missingCount} required detail${missingCount === 1 ? '' : 's'} still missing.`}
          </p>
        </div>
        {!isReady && (
          <button
            type="button"
            className="shrink-0 rounded-lg bg-brand-secondary px-6 py-3 text-neutral-00"
            onClick={() => onFinish(finishPath)}
            aria-label="Finish before your trip"
          >
            Finish before your trip
          </button>
        )}
      </div>
    </div>
  );
};

export default ProfileReadinessBanner;
